import { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Box } from '@zillow/constellation-10';

import { getPhotoUrls } from '../../../../utils/propertiesPageUtils';

import {
  StyledBackgroundImage,
  StyledBackgroundImageContainer,
  StyledBackgroundLoadingImage,
  StyledIconNote,
  StyledIconWarning,
  StyledListItem,
} from './StyledComponents';

import type { PhotoProps } from '@zg-rentals/maintenance-utils';

const ICON_TITLE_PHOTO_ERROR_MAP: Record<string, string> = {
  dmca: 'DMCA photo error',
  inappropriate: 'Inappropriate photo error',
};

const getUrlSrc = (url: string, errorDto: PhotoProps['errorDto'], isRunningInProd: boolean) => {
  if (!errorDto?.errorCode) {
    return url;
  }

  if (errorDto?.replacementPhotoId) {
    return getPhotoUrls(isRunningInProd, errorDto?.replacementPhotoId).thumb;
  }

  return '';
};

type Error = {
  photoId: string;
};

type Props = {
  photo: Partial<PhotoProps>;
  index: number;
  handlePhotoClick?: (arg: Partial<PhotoProps>) => void;
  errors?: Array<Error>;
  isUploading?: boolean;
  isDisabled?: boolean;
  isRunningInProd?: boolean;
};

// eslint-disable-next-line react/display-name
const SortableItem = memo(
  ({ handlePhotoClick, photo, errors, isUploading, index, isDisabled = false, isRunningInProd = false }: Props) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: photo.id || '',
      disabled: isDisabled,
    });

    if (isUploading) {
      return (
        <Box css={{ gridColumnEnd: 'span 1' }}>
          <StyledBackgroundImageContainer>
            <StyledBackgroundLoadingImage dark={true} type="box" />
          </StyledBackgroundImageContainer>
        </Box>
      );
    }

    const hasPhotoCaptionError = (errors || []).filter((error) => error.photoId === photo.id).length > 0 || photo.error;
    const photoNumber = handlePhotoClick ? `photo-${index + 1}` : `placeholder-photo-${index + 1}`;

    const renderIcon = () => {
      // NOTE: errorDto should take priority over isAppropriate
      if (photo?.errorDto?.errorCode) {
        return (
          <StyledIconWarning
            fontColor="orange400"
            size="sm"
            aria-hidden="false"
            aria-describedby={`icon-warning-title-photo-${photoNumber} icon-warning-desc-${photoNumber}`}
          >
            <title id={`icon-warning-title-${photoNumber}`}>
              {ICON_TITLE_PHOTO_ERROR_MAP[photo?.errorDto?.errorCode] || 'Photo error'}
            </title>
            <desc id={`icon-warning-desc-${photoNumber}`}>
              {photo?.errorDto?.errorMessage
                ? photo?.errorDto?.errorMessage
                : 'Button to view inappropriate or DMCA photo error'}
            </desc>
          </StyledIconWarning>
        );
      }

      // isAppropriate is deprecated and left in as fallback
      if (photo.isAppropriate === false) {
        return (
          <StyledIconWarning
            fontColor="orange400"
            size="sm"
            aria-hidden="false"
            aria-describedby={`icon-warning-title-photo-${photoNumber} icon-warning-desc-${photoNumber}`}
          >
            <title id={`icon-warning-title-${photoNumber}`}>Inappropriate Photo Error</title>
            <desc id={`icon-warning-desc-${photoNumber}`}>Button to view inappropriate photo error</desc>
          </StyledIconWarning>
        );
      }

      if (hasPhotoCaptionError) {
        return (
          <StyledIconWarning
            fontColor="orange400"
            size="sm"
            aria-hidden="false"
            aria-describedby={`icon-warning-title-photo-${photoNumber} icon-warning-desc-${photoNumber}`}
          >
            <title id={`icon-warning-title-${photoNumber}`}>Caption Error</title>
            <desc id={`icon-warning-desc-${photoNumber}`}>Button to view or fix caption error</desc>
          </StyledIconWarning>
        );
      }

      if (photo.caption) {
        return (
          <StyledIconNote
            fontColor="white"
            size="sm"
            aria-hidden="false"
            aria-describedby={`icon-caption-title-${photoNumber} icon-caption-desc-${photoNumber}`}
          >
            <title id={`icon-caption-title-${photoNumber}`}>Caption</title>
            <desc id={`icon-caption-desc-${photoNumber}`}>Button to view or edit caption</desc>
          </StyledIconNote>
        );
      }

      return null;
    };

    return (
      <StyledListItem
        isDisabled={isDisabled}
        aria-label={`Edit ${photoNumber}${photo.caption ? `; hasCaption: ${photo.caption}` : ''}`}
      >
        <button
          disabled={isDisabled}
          type="button"
          ref={setNodeRef}
          style={{
            transform: CSS.Transform.toString(transform),
            transition,
            padding: 0,
            borderRadius: '4px',
            width: '100%',
          }}
          {...attributes}
          {...listeners}
          onClick={() => handlePhotoClick?.({ ...photo })}
        >
          <Box css={{ gridColumnEnd: 'span 1' }}>
            <StyledBackgroundImageContainer>
              <StyledBackgroundLoadingImage dark={true} type="box" />
              <StyledBackgroundImage
                hasError={Boolean(hasPhotoCaptionError)}
                urlSrc={getUrlSrc(photo?.thumbUrl || photo?.url || '', photo?.errorDto, isRunningInProd)}
                alt="text"
                isDisabled={isDisabled}
              >
                {renderIcon()}
              </StyledBackgroundImage>
            </StyledBackgroundImageContainer>
          </Box>
        </button>
      </StyledListItem>
    );
  },
);

export default SortableItem;
